import React, { useEffect, useState } from 'react';
import { FileInput, FileField } from 'react-admin';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import GetAppIcon from '@material-ui/icons/GetApp';

const PREVIEW_LINES = 100;

function authHeaders () {
  const token = localStorage.getItem('token');
  const headers = new Headers({ Accept: 'application/json' });
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

/**
 * Fire a GET to /:resource/:code/csv and trigger a browser save dialog.
 * Uses fetch+blob (not a plain anchor href) so we can attach the bearer token.
 */
async function downloadCsv (code, resource = 'ip_lists') {
  const res = await fetch(`${window.nobid_endpoint}/${resource}/${code}/csv`, { headers: authHeaders() });
  if (!res.ok) {
    alert(`Failed to download CSV: ${res.status} ${res.statusText}`);
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ip_list_${code}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Reusable CSV upload widget.
 *
 * Renders a react-admin FileInput bound to the given form `source`. The
 * App.js dataProvider interceptor reads the `rawFile` off the form data and
 * sends it to the backend as a base64 `file_data` field on create/update.
 *
 * When `code` is provided AND the list has a previously-uploaded CSV
 * (`existingKey` non-null), also renders:
 *   - a Download button that pulls the full CSV with auth headers,
 *   - a preview block showing the first N lines (fetched via ?preview=N
 *     so we only transfer ~4 KB regardless of file size).
 *
 * `resource` defaults to 'ip_lists' (the ops resource) so the existing
 * caller (src/ipLists.js) is unchanged; the advertiser screen
 * (src/advertiser/ip_lists.js) passes resource="advertiser_ip_lists" so the
 * download/preview requests hit the advertiser-scoped endpoint instead of a
 * second copy of this component.
 */
export const CsvUploadComponent = ({
  source = 'file_upload',
  label = 'Upload CSV',
  helperText = 'CSV with one entry per line. Each line is either a single IP (e.g. 1.2.3.4) or a range (e.g. 1.2.3.0,1.2.3.255). IPv4 and IPv6 supported.',
  existingKey,
  code,
  resource = 'ip_lists',
}) => {
  const hasFile = Boolean(existingKey && code);
  const [preview, setPreview] = useState({ status: 'idle', lines: [], totalBytes: null, error: null });

  useEffect(() => {
    if (!hasFile) return;
    let cancelled = false;
    setPreview({ status: 'loading', lines: [], totalBytes: null, error: null });
    fetch(`${window.nobid_endpoint}/${resource}/${code}/csv?preview=${PREVIEW_LINES}`, { headers: authHeaders() })
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`${res.status} ${res.statusText}`)))
      .then(json => {
        if (cancelled) return;
        setPreview({ status: 'loaded', lines: json.lines || [], totalBytes: json.total_bytes, error: null });
      })
      .catch(err => {
        if (cancelled) return;
        setPreview({ status: 'error', lines: [], totalBytes: null, error: err.message });
      });
    return () => { cancelled = true; };
  }, [code, existingKey, hasFile, resource]);

  return (
    <Box style={{ width: '100%', marginBottom: '16px' }}>
      {hasFile && (
        <Box style={{ marginBottom: '12px' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
              Current file: <code>{existingKey}</code>
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={() => downloadCsv(code, resource)}
            >
              Download
            </Button>
          </Box>
          <Typography variant="caption" color="textSecondary">
            Uploading a new file will replace the current one.
          </Typography>

          <Box style={{ marginTop: '12px' }}>
            <Typography variant="subtitle2">Preview (first {PREVIEW_LINES} rows)</Typography>
            {preview.status === 'loading' && (
              <Box style={{ padding: '8px' }}>
                <CircularProgress size={18} />
              </Box>
            )}
            {preview.status === 'error' && (
              <Typography variant="caption" color="error">Failed to load preview: {preview.error}</Typography>
            )}
            {preview.status === 'loaded' && (
              <Box
                component="pre"
                style={{
                  background: '#f5f5f5', color: '#222',
                  padding: '8px 12px', borderRadius: '4px',
                  fontSize: '12px', lineHeight: '1.6', maxHeight: '280px', overflow: 'auto',
                  margin: '4px 0',
                  border: '1px solid #ddd',
                }}
              >
                {preview.lines.length === 0 ? '(empty file)' : preview.lines.join('\n')}
              </Box>
            )}
            {preview.totalBytes != null && preview.status === 'loaded' && (
              <Typography variant="caption" color="textSecondary">
                Total file size: {preview.totalBytes.toLocaleString()} bytes
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <FileInput
        source={source}
        label={label}
        accept=".csv,text/csv"
        helperText={helperText}
        multiple={false}
      >
        <FileField source="src" title="title" />
      </FileInput>
    </Box>
  );
};

export default CsvUploadComponent;
